// Sun tool: a topbar button + popover to set the time of day (drives Cesium's sun, which
// lights the globe + 3D models). Defaults to a flattering mid-morning sun so models read well;
// a slider scrubs the time, presets jump to 10 AM / 2 PM, and "Real time" tracks the actual sun.
import { JulianDate } from "cesium";
import type { Viewer } from "cesium";

const WA_OFFSET = 8; // Pilbara local time = UTC+8
const KEY = "geaar.sun"; // persisted { mode: "fixed" | "real", hour: number }
let realtimeTimer: number | null = null;

function clearTimer(): void {
  if (realtimeTimer != null) {
    clearInterval(realtimeTimer);
    realtimeTimer = null;
  }
}

/** Set the sun to a WA-local hour today (e.g. 10.5 = 10:30 AM local). */
function applyLocalHour(viewer: Viewer, hour: number): void {
  clearTimer();
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  d.setUTCHours(Math.floor(hour) - WA_OFFSET, Math.round((hour % 1) * 60), 0, 0);
  viewer.clock.shouldAnimate = false;
  viewer.clock.currentTime = JulianDate.fromDate(d);
  viewer.scene.requestRender();
}

/** Track the real sun, refreshing once a minute (cheap; requestRenderMode stays on). */
function applyRealtime(viewer: Viewer): void {
  const tick = (): void => {
    viewer.clock.currentTime = JulianDate.now();
    viewer.scene.requestRender();
  };
  tick();
  if (realtimeTimer == null) realtimeTimer = window.setInterval(tick, 60000);
}

function fmt(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour % 1) * 60);
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export function mountSunTool(topbar: HTMLElement, viewer: Viewer, defaultHour = 10): void {
  let saved: { mode?: string; hour?: number } = {};
  try {
    saved = JSON.parse(localStorage.getItem(KEY) || "{}") || {};
  } catch {
    saved = {};
  }
  let hour = typeof saved.hour === "number" ? saved.hour : defaultHour;
  let realtime = saved.mode === "real";

  const btn = document.createElement("button");
  btn.className = "tool-btn sun-btn";
  btn.title = "Set the time of day (sun position / lighting)";
  topbar.append(btn);

  const pop = document.createElement("div");
  pop.className = "sun-pop";
  pop.style.display = "none";
  pop.innerHTML = `
    <div class="sun-head">Sun <span class="sun-time"></span></div>
    <input class="sun-slider" type="range" min="5" max="19" step="0.25" />
    <div class="sun-presets">
      <button class="sun-preset" data-h="10">10 AM</button>
      <button class="sun-preset" data-h="14">2 PM</button>
      <button class="sun-real">Real time</button>
    </div>`;
  document.body.append(pop);
  const timeLbl = pop.querySelector(".sun-time") as HTMLElement;
  const slider = pop.querySelector(".sun-slider") as HTMLInputElement;
  const realBtn = pop.querySelector(".sun-real") as HTMLButtonElement;

  const refresh = (): void => {
    slider.value = String(hour);
    slider.disabled = realtime;
    timeLbl.textContent = realtime ? "Real time" : fmt(hour);
    realBtn.classList.toggle("active", realtime);
    btn.textContent = realtime ? "☀ Live" : `☀ ${fmt(hour)}`;
    localStorage.setItem(KEY, JSON.stringify({ mode: realtime ? "real" : "fixed", hour }));
  };
  const apply = (): void => {
    if (realtime) applyRealtime(viewer);
    else applyLocalHour(viewer, hour);
    refresh();
  };

  slider.addEventListener("input", () => {
    realtime = false;
    hour = parseFloat(slider.value);
    apply();
  });
  pop.querySelectorAll<HTMLButtonElement>(".sun-preset").forEach((b) =>
    b.addEventListener("click", () => {
      realtime = false;
      hour = parseFloat(b.dataset.h || "10");
      apply();
    }),
  );
  realBtn.addEventListener("click", () => {
    realtime = !realtime;
    apply();
  });

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const open = pop.style.display === "none";
    if (open) {
      const r = btn.getBoundingClientRect();
      pop.style.top = `${r.bottom + 6}px`;
      pop.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
    }
    pop.style.display = open ? "block" : "none";
  });
  document.addEventListener("click", (e) => {
    if (!pop.contains(e.target as Node) && !btn.contains(e.target as Node)) pop.style.display = "none";
  });

  apply(); // set the saved/default sun on load
}
